import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { CYCLE_OPEN, dateTokenLabel, readDateToken } from '../../lists';

/**
 * A date literal — a day from a date picker **or** a named day from a dropdown (SR-230, design
 * `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md` §3.4): the funnel's Since, Before
 * and Between, and a date condition's value in *Advanced…*. What it holds is what the leaf sends —
 * an ISO day or a token as `ListDateTokens` spells it — and it resolves nothing: "the last 30
 * days" is survey-api's to turn into days, against its own clock and zone.
 *
 * The dropdown offers the design's list (LST-05 AC2's "edited since", Conga's relative dates in
 * words); a stored token outside it — `next_n_weeks:3`, written by a migration or a colleague's
 * builder — is offered too, in its words, so opening a view never changes its filter. "The cycle
 * opened" only where the operator takes it (`since`, `before`; survey-api refuses it elsewhere).
 */

const NAMED: readonly string[] = [
  'today',
  'yesterday',
  'this_week',
  'last_week',
  'this_month',
  'last_month',
  'this_quarter',
  'this_year',
  'last_n_days:7',
  'last_n_days:30',
  'last_n_days:90'
];

/** The dropdown's own value for "a day from the picker" — never a token survey-api spells. */
const A_DAY = '#day';

/** A token's words, capitalised for the dropdown: "The last 30 days", "This week". */
function namedLabel(token: string): string {
  const words = dateTokenLabel(token);
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function DateLiteral({
  label,
  value,
  allowCycle,
  invalid,
  onChange
}: {
  /** The pair's accessible name — "Since", "Condition 2 — Value". */
  label: string;
  /** An ISO day, a token, `''` for none yet, or a stored value of another shape (as text). */
  value: string;
  allowCycle: boolean;
  invalid?: boolean;
  onChange: (value: string) => void;
}) {
  const token = value === '' ? null : readDateToken(value);
  const isDay = value === '' || token?.kind === 'day';
  // A value that reads as no token at all is kept as typed, in the day box, for survey-api's 400.
  const asTyped = value !== '' && token === null;
  const named = [...NAMED, ...(allowCycle ? [CYCLE_OPEN] : [])];
  const offered = !isDay && !asTyped && !named.includes(value) ? [...named, value] : named;
  const [picking, setPicking] = React.useState(isDay || asTyped);
  const choice = picking || isDay || asTyped ? A_DAY : value;

  return (
    <span className="inline-flex flex-wrap items-center gap-1.5">
      <Select
        value={choice}
        onValueChange={(next) => {
          if (next === A_DAY) {
            setPicking(true);
            if (!isDay && !asTyped) onChange('');
            return;
          }
          setPicking(false);
          onChange(next);
        }}
      >
        <SelectTrigger aria-label={`${label} — named day`} className="h-8 w-44">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={A_DAY}>A day…</SelectItem>
          {offered.map((one) => (
            <SelectItem key={one} value={one}>
              {namedLabel(one)}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {choice === A_DAY && (
        <Input
          type={asTyped ? 'text' : 'date'}
          aria-label={label}
          aria-invalid={invalid || undefined}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="h-8 w-40"
        />
      )}
    </span>
  );
}
